import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Select, Spin, Typography } from 'antd';
import debounce from 'lodash/debounce';
import userService from '../services/userService';

const { Text } = Typography;

/**
 * 用户选择器组件
 * 支持搜索和分页加载
 */
const UserSelect = ({
    value,
    onChange,
    placeholder = '选择用户',
    style,
    disabled = false,
    allowClear = true,
    ...restProps
}) => {
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);
    const [searchValue, setSearchValue] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const pageSize = 20;
    const fetchRef = useRef(0);

    // 加载用户
    const loadUsers = useCallback(async (search = '', pageNum = 1, append = false) => {
        const fetchId = ++fetchRef.current;
        setLoading(true);

        try {
            const response = await userService.getUsers({
                page: pageNum,
                page_size: pageSize,
                search: search || undefined
            });

            if (fetchId !== fetchRef.current) return;

            if (response.success) {
                const newUsers = response.data?.data || response.data || [];
                const total = response.data?.total || newUsers.length;

                if (append) {
                    setUsers(prev => [...prev, ...newUsers]);
                } else {
                    setUsers(newUsers);
                }

                setHasMore(pageNum * pageSize < total);
                setPage(pageNum);
            }
        } catch (error) {
            console.error('Failed to load users:', error);
        } finally {
            if (fetchId === fetchRef.current) {
                setLoading(false);
            }
        }
    }, []);

    // 防抖搜索
    const debouncedSearch = useCallback(
        debounce((search) => {
            setSearchValue(search);
            loadUsers(search, 1, false);
        }, 300),
        [loadUsers]
    );

    // 初始加载
    useEffect(() => {
        loadUsers('', 1, false);
    }, [loadUsers]);

    // 如果有初始值，尝试加载该用户信息
    useEffect(() => {
        if (value && !selectedUser) {
            // 从已加载的用户中查找
            const found = users.find(u => u.id === value);
            if (found) {
                setSelectedUser(found);
            } else {
                // 单独加载该用户
                userService.getUser(value).then(resp => {
                    if (resp.success && resp.data) {
                        setSelectedUser(resp.data);
                    }
                }).catch(() => {});
            }
        }
    }, [value, users, selectedUser]);

    const handleSearch = (search) => {
        debouncedSearch(search);
    };

    const handlePopupScroll = (e) => {
        const { target } = e;
        if (
            !loading &&
            hasMore &&
            target.scrollTop + target.offsetHeight >= target.scrollHeight - 50
        ) {
            loadUsers(searchValue, page + 1, true);
        }
    };

    const handleChange = (newValue) => {
        if (newValue) {
            const user = users.find(u => u.id === newValue);
            setSelectedUser(user);
        } else {
            setSelectedUser(null);
        }
        onChange?.(newValue);
    };

    const options = users.map(user => ({
        value: user.id,
        label: (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>
                    <Text strong>{user.username}</Text>
                    {user.email && (
                        <Text type="secondary" style={{ marginLeft: 8 }}>
                            {user.email}
                        </Text>
                    )}
                </span>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    ID: {user.id}
                </Text>
            </div>
        )
    }));

    return (
        <Select
            showSearch
            value={value}
            onChange={handleChange}
            onSearch={handleSearch}
            onPopupScroll={handlePopupScroll}
            placeholder={placeholder}
            style={style}
            disabled={disabled}
            allowClear={allowClear}
            filterOption={false}
            notFoundContent={loading ? <Spin size="small" /> : '无匹配用户'}
            loading={loading}
            optionLabelProp="label"
            {...restProps}
        >
            {options.map(opt => (
                <Select.Option key={opt.value} value={opt.value} label={
                    selectedUser?.id === opt.value
                        ? `${selectedUser.username} (ID: ${selectedUser.id})`
                        : `用户 ${opt.value}`
                }>
                    {opt.label}
                </Select.Option>
            ))}
            {loading && hasMore && (
                <Select.Option disabled key="loading">
                    <Spin size="small" style={{ marginRight: 8 }} />
                    加载中...
                </Select.Option>
            )}
        </Select>
    );
};

export default UserSelect;
